import type { ChargePointSummary } from '../../../../../app/ApiGen';
import { TableDataClass } from '../../../../../design-system';
import { convertToKwh, type KiloWattHours } from '../../../../../utils/format';
import { roundTo2DecimalPlaces, sumValues } from '../../../../../utils/number';
import type { Normalised } from '../../../../../utils/request';
import { calculateReimbursementRate, costOrLegacyCostOrDefault } from '../../../../../utils/sessions';
import { isNormalisedSessionWithChargePoint, type NormalisedSession } from './types';

export class ChargePointSessionSummary extends TableDataClass {
  public readonly chargePoint: Omit<Normalised<ChargePointSummary>, 'electricityCost' | 'referenceId'>;

  public readonly sessions: NormalisedSession[];

  public readonly currency: string;

  private readonly forAsb: boolean;

  constructor(args: { chargePoint: Normalised<ChargePointSummary>; forAsb?: boolean }) {
    super();
    const { chargePoint, forAsb } = args;
    this.chargePoint = chargePoint;
    this.currency =
      (chargePoint.chargingConfiguration?.currency as string | undefined) ??
      chargePoint.electricityCost?.currency ??
      'NZD';
    this.sessions = [];
    this.forAsb = forAsb ?? false;
  }

  public addSession(session: NormalisedSession): void {
    if (session.chargePoint.id !== this.chargePoint.id) {
      throw new Error('Cannot add session for a different charge point');
    }
    this.sessions.push(session);
  }

  /**
   * Return the total charging cost of the session in dollars and cents
   **/
  public get cost(): number {
    return roundTo2DecimalPlaces(
      this.sessions
        .map<number>((session) => costOrLegacyCostOrDefault({ session }))
        .reduce((first, second) => first + second, 0),
    );
  }

  /**
   * Return the total reimbursement figure of the session in dollars and cents
   **/
  public get reimbursementFigure(): number | undefined {
    if (!this.forAsb) {
      return undefined;
    }

    const sessionCosts = this.sessions.map<number>((session) => calculateReimbursementRate({ session }) ?? 0);

    return roundTo2DecimalPlaces(sessionCosts.reduce((first, second) => first + second, 0));
  }

  /**
   * Return the total energy used in the session in kWh
   **/
  public get totalUsage(): KiloWattHours {
    const totalEnergyUsages = this.sessions.flatMap((session) => session.totalPowerUsage ?? 0);
    return Math.round(convertToKwh(sumValues(totalEnergyUsages)));
  }

  public get totalCarbonUsage(): string | undefined {
    const carbonUse = this.sessions.flatMap((session) => session.totalCarbonUsage ?? []);
    if (carbonUse.length > 0) {
      return sumValues(carbonUse).toLocaleString(undefined, { maximumFractionDigits: 0 });
    }
    return undefined;
  }
}

type ChargePointId = string;

export function toChargePointSessionSummaries(args: {
  sessions: NormalisedSession[];
  forAsb?: boolean;
}): ChargePointSessionSummary[] {
  const { forAsb, sessions } = args;
  const sessionsByChargePointId = sessions
    .filter(isNormalisedSessionWithChargePoint)
    .reduce<Map<ChargePointId, ChargePointSessionSummary>>((chargePointSessions, session) => {
      const existingSummary = chargePointSessions.get(session.chargePoint.id);
      if (typeof existingSummary === 'undefined') {
        const summary = new ChargePointSessionSummary({ chargePoint: session.chargePoint, forAsb });
        summary.addSession(session);
        chargePointSessions.set(summary.chargePoint.id, summary);
      } else {
        existingSummary.addSession(session);
      }
      return chargePointSessions;
    }, new Map());
  return Array.from(sessionsByChargePointId.values());
}
